import { Info } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const InfoTip = ({ text, testId }) => (
  <TooltipProvider delayDuration={80}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-grid place-items-center h-5 w-5 rounded-full text-slate-400 hover:text-[#FACC15] focus:text-[#FACC15] cursor-help shrink-0" data-testid={testId} onClick={(e) => e.preventDefault()}>
          <Info className="h-4 w-4" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm whitespace-pre-line bg-[#0B0F16] text-slate-100 border border-white/15 text-sm leading-snug px-3 py-2">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const LabelRow = ({ label, info, testId }) => (
  <div className="flex items-center gap-1.5 min-h-5">
    <Label className="text-slate-300">{label}</Label>
    {info && <InfoTip text={info} testId={testId && `${testId}-info`} />}
  </div>
);

export const SettingSelect = ({ label, info, value, onChange, options, testId, disabled }) => (
  <div className="space-y-2">
    <LabelRow label={label} info={info} testId={testId} />
    <Select value={String(value)} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-11" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const SettingNumber = ({ label, info, value, onChange, testId, min = 0, max = 100000, step = 1, wide = true }) => (
  <div className={`space-y-2 ${wide ? "sm:col-span-2" : ""}`}>
    <LabelRow label={label} info={info} testId={testId} />
    <Input type="number" min={min} max={max} step={step} value={value} className="h-11" data-testid={testId}
      onChange={(e) => onChange(Math.min(max, Math.max(min, Number(e.target.value) || 0)))} />
  </div>
);

export const SettingSwitch = ({ label, info, checked, onChange, testId }) => (
  <label className="sm:col-span-2 flex items-center justify-between gap-3 rounded-xl border border-[rgba(71,85,105,0.45)] px-4 h-12 cursor-pointer">
    <span className="flex items-center gap-1.5 text-slate-200 text-sm">{label}{info && <InfoTip text={info} testId={testId && `${testId}-info`} />}</span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </label>
);

const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => [a + i, String(a + i)]);

export const RoomSettingsForm = ({ settings, onChange, lobby = true, ModeForm }) => {
  const closed = Boolean(settings.closed_session);
  return (
    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-4" data-testid="room-settings-form">
      {closed && <p className="sm:col-span-2 text-sm text-[#FACC15]" data-testid="closed-session-note">Kapalı oturum açık: takım ve öğrenci ayarları kullanılmaz, oyun tahtada sınıfça oynanır.</p>}
      {closed && <SettingNumber label="Sınıfın hedef puanı" info="Sınıfın bu oyunda ulaşmaya çalışacağı toplam puan. Tahtada hedefe kalan puan ilerleme çubuğuyla gösterilir. 0 yazarsanız hedef kullanılmaz."
        value={settings.class_target || 0} step={10} testId="setting-class-target" onChange={(v) => onChange({ class_target: v })} />}
      {!closed && <SettingNumber label="Doğru cevabı gösterme süresi (sn)" info="Bağlı tüm öğrenciler soruyu yanıtlayınca tahtada 'Tüm öğrenciler yanıtladı' yazar, doğru cevap bu süre kadar ekranda kalır ve sonraki soruya kendiliğinden geçilir. 0 yazarsanız otomatik geçiş kapanır."
        value={settings.reveal_seconds ?? 5} max={60} testId="setting-reveal-seconds" onChange={(v) => onChange({ reveal_seconds: v })} />}
      <SettingSelect label="Takım sayısı" info="Sınıf kaç takıma ayrılsın? 'Kapalı' seçilirse herkes bireysel yarışır, puanlar kişiye yazılır." value={closed ? 0 : settings.team_count} disabled={!lobby || closed} testId="setting-team-count"
        onChange={(v) => onChange({ team_count: Number(v) })} options={[[0, "Kapalı (bireysel)"], ...range(2, 6)]} />
      {settings.team_count > 0 && !closed && <>
        <SettingSelect label="Masa kapasitesi" info="Her takım masasına en fazla kaç öğrenci oturabilir." value={settings.seats_per_team} disabled={!lobby} testId="setting-seats"
          onChange={(v) => onChange({ seats_per_team: Number(v) })} options={range(2, 15)} />
        <SettingSelect label="Takım / masa seçimi" info="Öğrenciler masasını kendisi mi seçsin, sistem dengeli mi dağıtsın, yoksa siz mi atayın." value={settings.team_selection} disabled={!lobby} testId="setting-team-selection"
          onChange={(v) => onChange({ team_selection: v })}
          options={[["student", "Öğrenci masasını seçer"], ["auto", "Otomatik dengeli dağıtım"], ["teacher", "Öğretmen atar"]]} />
      </>}
      <SettingSelect label="Tahtada isim" info="Lobide ve tahtada öğrenci adlarının nasıl görüneceği." value={settings.board_names} disabled={closed} testId="setting-board-names"
        onChange={(v) => onChange({ board_names: v })}
        options={[["initial", "Ad + soyadın baş harfi"], ["full", "Tam isim"], ["none", "Hiç gösterme"]]} />
      {ModeForm && <ModeForm settings={settings} onChange={onChange} />}
      <SettingSwitch label="Tahtada ses efektleri" info="Katılım, doğru/yanlış ve geri sayım sesleri tahtada çalar." checked={settings.sound !== false} onChange={(v) => onChange({ sound: v })} testId="setting-sound" />
      <SettingSwitch label="Animasyonlar" info="Konfeti, podyum ve doğru/yanlış efektleri gösterilir." checked={settings.animations !== false} onChange={(v) => onChange({ animations: v })} testId="setting-animations" />
      <SettingSwitch label="Kutlama müziği" info="Oyun sonunda podyuma çıkarken 10 saniyelik kutlama müziği döngüde çalar." checked={settings.celebration_music !== false} onChange={(v) => onChange({ celebration_music: v })} testId="setting-celebration-music" />
      <SettingSwitch label="Kapalı oturum" info="Öğrenciler telefonla katılmaz; oyun tahtada sınıfça oynanır, sorular tahtadan yanıtlanır ve puan sınıfa yazılır. Takım ayarları kapanır." checked={closed} onChange={(v) => onChange(v ? { closed_session: true, team_count: 0 } : { closed_session: false })} testId="setting-closed-session" />
    </div>
  );
};
